import React, { useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { Link, useLocation, useHistory } from "react-router-dom";

import {
  SparklesIcon,
  InboxIcon,
  PlusIcon,
  RocketLaunchIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import { useProfile, useActivePromo } from "@moonscape/shared";
import { useAccountMe } from "@/hooks/useAccountMe";
import { useInboxUnreadCount } from "@/hooks/useInbox";
import { buildNavigation, isNavActive } from "@/navigation";
import { useFeatureFlag } from "@moonscape/shared";
import { FEATURE_FLAG_KEYS } from "@moonscape/shared";
import { useReadOnly } from "@/contexts";
import { Tooltip } from "@/components/ui/tooltip";
import { Logo, Tag } from "@/components/ms";

/* Rail at 72px always below 1100. At 1100+, labelled by default but user can collapse back to rail. */
const ITEM_ON = "text-ms-ink border-l-ms-rule";

export const DesktopSidebarV2 = () => {
  const location = useLocation();
  const history = useHistory();
  const readOnly = useReadOnly();

  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem("sidebarCollapsed") === "true"
  );
  useEffect(() => {
    localStorage.setItem("sidebarCollapsed", String(collapsed));
    document.body.classList.toggle("ms-sidebar-collapsed", collapsed);
    return () => document.body.classList.remove("ms-sidebar-collapsed");
  }, [collapsed]);

  const itemClass = (active: boolean) =>
    clsx(
      "flex h-11 w-11 items-center justify-center border-l-2 border-transparent text-ms-dim transition-colors hover:text-ms-ink",
      !collapsed && "ms1100:h-auto ms1100:w-auto ms1100:justify-start ms1100:gap-[11px] ms1100:px-2.5 ms1100:py-[11px]",
      active && ITEM_ON
    );
  const labelClass = clsx(
    "hidden text-[14px] font-medium tracking-[-0.01em]",
    !collapsed && "ms1100:inline"
  );

  const { profile, isAccountant } = useProfile();
  const quizCompleted = Boolean(
    profile?.attributes?.quiz?.completedAt || localStorage.getItem("quizCompletedAt")
  );
  const { hasActivePromo, promo, timing } = useActivePromo();
  const { data: me } = useAccountMe();
  const { data: unread = 0 } = useInboxUnreadCount();

  const showIAP = useFeatureFlag(FEATURE_FLAG_KEYS.WEB_SHOW_IAP);
  const showSafeHarbor = useFeatureFlag(FEATURE_FLAG_KEYS.WEB_SAFE_HARBOR);

  const navigation = useMemo(() => {
    const featureFlags = {
      [FEATURE_FLAG_KEYS.WEB_SHOW_IAP]: showIAP,
      [FEATURE_FLAG_KEYS.WEB_SAFE_HARBOR]: showSafeHarbor,
    };
    const userRoles = isAccountant ? ["accountant"] : [];
    return buildNavigation(featureFlags, userRoles);
  }, [showIAP, showSafeHarbor, isAccountant]);

  const hrefs = useMemo(
    () => [...navigation.main, ...navigation.bottom].map((n) => n.href),
    [navigation]
  );
  const on = (href: string) => isNavActive(location?.pathname ?? "", href, hrefs);

  const item = (href: string, active: boolean, icon: any, name: string, right?: React.ReactNode) => {
    const Icon = icon;
    return (
      <Tooltip key={href} title={name} placement="right">
        <Link to={href} className={itemClass(active)}>
          <Icon className="h-[19px] w-[19px] flex-shrink-0" />
          <span className={labelClass}>{name}</span>
          {right ? (
            <span className={clsx("ml-auto hidden", !collapsed && "ms1100:inline")}>{right}</span>
          ) : null}
        </Link>
      </Tooltip>
    );
  };

  return (
    <nav
      className={clsx(
        "ms-a-side sticky top-0 hidden h-screen flex-col border-r border-ms-line bg-ms-bg ms700:flex ms700:items-center ms700:py-3.5 print:!hidden",
        !collapsed && "ms1100:items-stretch ms1100:px-3.5 ms1100:py-4"
      )}
    >
      {/* logo — fixed-height header, same box whether collapsed or not, so toggling doesn't reflow anything below it */}
      <div
        className={clsx(
          "mb-3 flex h-11 w-full items-center justify-center border-b border-ms-line",
          !collapsed && "ms1100:justify-start"
        )}
      >
        <Link to="/" className="flex items-center gap-2.5 text-ms-ink">
          <Logo width={22} />
          <span
            className={clsx(
              "hidden text-[15px] font-bold tracking-[-0.015em]",
              !collapsed && "ms1100:inline"
            )}
          >
            Moonscape
          </span>
        </Link>
      </div>

      {/* collapse toggle — only relevant once the labelled layout is reachable */}
      <button
        type="button"
        onClick={() => setCollapsed((c) => !c)}
        aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        className="mb-2 hidden h-7 w-7 items-center justify-center rounded-full text-ms-dim transition-colors hover:text-ms-ink ms1100:flex"
      >
        {collapsed ? (
          <ChevronRightIcon className="h-4 w-4" />
        ) : (
          <ChevronLeftIcon className="h-4 w-4" />
        )}
      </button>

      <div className="scroll-thin flex flex-1 flex-col gap-1.5 overflow-y-auto ms700:items-center ms1100:items-stretch">
        {navigation.main.map((nav) => item(nav.href, on(nav.href), nav.icon, nav.name))}

        {item(
          "/inbox",
          on("/inbox"),
          InboxIcon,
          "Inbox",
          unread > 0 ? <Tag tone="neg">{unread > 99 ? "99+" : unread}</Tag> : undefined
        )}

        {me?.is_admin &&
          item("/admin/inbox", on("/admin/inbox"), SparklesIcon, "Admin Inbox")}

        {!quizCompleted && item("/quiz", on("/quiz"), SparklesIcon, "Get started")}

        <div className="flex-1" />

        {navigation.bottom
          .filter((i) => i.name !== "Refer a friend") // Hide referral for now
          .map((nav) =>
            item(
              nav.href,
              nav.name === "Settings"
                ? location?.pathname.startsWith("/settings")
                : on(nav.href),
              nav.icon,
              nav.name
            )
          )}

        {hasActivePromo &&
          item(
            "/promo",
            on("/promo"),
            SparklesIcon,
            `${promo?.discountPercent}% off — claim${
              timing?.secondsRemaining && timing.secondsRemaining < 3600
                ? ` (${Math.floor(timing.secondsRemaining / 60)}m)`
                : ""
            }`
          )}

        {showIAP && !hasActivePromo &&
          item("/plans", on("/plans"), RocketLaunchIcon, "Upgrade plan")}
      </div>

      {/* Add source — round at rail width, pill when labelled */}
      <Tooltip title="Add a source" placement="right">
        <button
          type="button"
          disabled={readOnly}
          onClick={() => history.push("/settings/exchange/new")}
          className={clsx(
            "my-2 grid h-10 w-10 place-items-center rounded-full bg-ms-pill-bg text-ms-pill-ink transition-colors hover:bg-ms-pill-bg-hover disabled:opacity-50",
            !collapsed &&
              "ms1100:mt-3 ms1100:flex ms1100:h-auto ms1100:w-auto ms1100:items-center ms1100:justify-center ms1100:gap-[9px] ms1100:rounded-full ms1100:px-3.5 ms1100:py-[11px]"
          )}
        >
          <PlusIcon className="h-[18px] w-[18px] flex-shrink-0" />
          <span className={clsx("hidden text-[14px] font-semibold", !collapsed && "ms1100:inline")}>
            Add source
          </span>
        </button>
      </Tooltip>
    </nav>
  );
};

export default DesktopSidebarV2;
